import { AssignmentDTOIsAdministratorDTO, AssignmentDTOIsCreativeDTO } from 'utils/typeguards'
import { Dispatch, FC, ReactNode, SetStateAction, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useEditAdminCommentsForEditor, useEditCreativeInstructionsFromAdmin, useEditInternalComments } from 'dataQueries'

import { Nullable } from 'models/helpers'
import { UseMutationResult } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { mutationToAPIRequest } from 'utils/reactQuery'
import { APIRequest } from 'models/API/APITypes'
import { useGalleryAssignment } from './GalleryAssignment.context'
import { useGalleryConstants } from './GalleryConstants.context'
import { useGalleryProduct } from './GalleryProduct.context'
import { useGalleryVirtualVisit } from './GalleryVirtualVisit.context'

interface GalleryCommentsInterface {
  adminCommentsForEditor: string
  setAdminCommentsForEditor: Dispatch<SetStateAction<string>>
  adminInternalComments: string
  setAdminInternalComments: Dispatch<SetStateAction<string>>
  creativeInstructionsFromAdmin: string
  setCreativeInstructionsFromAdmin: Dispatch<SetStateAction<string>>
  creativeCommentsForAdmin: string
  setCreativeCommentsForAdmin: Dispatch<SetStateAction<string>>
  adminCommentsForEditorResponse?: Nullable<APIRequest<any>>
  adminInternalCommentsResponse?: Nullable<APIRequest<any>>
  creativeInstructionsFromAdminResponse?: Nullable<APIRequest<any>>
  editAdminCommentsForEditorMutation: UseMutationResult<AxiosResponse, any, { assignmentId: string, comments: string }, any>
  editInternalCommentsMutation: UseMutationResult<AxiosResponse, any, { assignmentId: string, comments: string }, any>
  editCreativeInstructionsFromAdminMutation: UseMutationResult<AxiosResponse, any, { assignmentId: string, instructions: string }, any>
}

const defaultGalleryCommentsValue: GalleryCommentsInterface = {
  adminCommentsForEditor: '',
  setAdminCommentsForEditor: () => { throw new Error('setAdminCommentsForEditor is undefined') },
  adminInternalComments: '',
  setAdminInternalComments: () => { throw new Error('setAdminInternalComments is undefined') },
  creativeInstructionsFromAdmin: '',
  setCreativeInstructionsFromAdmin: () => { throw new Error('setCreativeInstructionsFromAdmin is undefined') },
  creativeCommentsForAdmin: '',
  setCreativeCommentsForAdmin: () => { throw new Error('setCreativeCommentsForAdmin is undefined') },
  editAdminCommentsForEditorMutation: {} as UseMutationResult<AxiosResponse, any, { assignmentId: string, comments: string }, any>,
  editInternalCommentsMutation: {} as UseMutationResult<AxiosResponse, any, { assignmentId: string, comments: string }, any>,
  editCreativeInstructionsFromAdminMutation: {} as UseMutationResult<AxiosResponse, any, { assignmentId: string, instructions: string }, any>,
}

/** Gallery comments context */
export const GalleryCommentsContext = createContext<GalleryCommentsInterface>(defaultGalleryCommentsValue)
/** Gallery comments context hook */
export const useGalleryComments = (): GalleryCommentsInterface => useContext(GalleryCommentsContext)

/** Context provider for gallery comments */
export const GalleryCommentsContextProvider: FC<{
  assignmentId: string
  children?: ReactNode
}> = ({
  assignmentId,
  children,
}) => {
    const {
      AvailableVirtualVisitLinkTypes,
    } = useGalleryConstants()

    const {
      assignmentData,
      assignment
    } = useGalleryAssignment()

    const {
      isFloorPlan,
    } = useGalleryProduct()

    const {
      virtualVisitURL,
      generateVirtualVisitShareLink,
    } = useGalleryVirtualVisit()

    const [adminCommentsForEditor, setAdminCommentsForEditor] = useState('')
    const [adminInternalComments, setAdminInternalComments] = useState('')
    const [creativeInstructionsFromAdmin, setCreativeInstructionsFromAdmin] = useState('')
    const [creativeCommentsForAdmin, setCreativeCommentsForAdmin] = useState('')

    const editAdminCommentsForEditorMutation = useEditAdminCommentsForEditor()
    const editInternalCommentsMutation = useEditInternalComments()
    const editCreativeInstructionsFromAdminMutation = useEditCreativeInstructionsFromAdmin()

    const adminCommentsForEditorResponse = useMemo(() => mutationToAPIRequest(editAdminCommentsForEditorMutation), [editAdminCommentsForEditorMutation])
    const adminInternalCommentsResponse = useMemo(() => mutationToAPIRequest(editInternalCommentsMutation), [editInternalCommentsMutation])
    const creativeInstructionsFromAdminResponse = useMemo(() => mutationToAPIRequest(editCreativeInstructionsFromAdminMutation), [editCreativeInstructionsFromAdminMutation])

    const assignmentAlreadyLoaded = useRef('')

    // Fill in comment / instruction states upon assignment received
    useEffect(() => {
      if (!assignment.isSuccess) return
      if (!assignmentData) return
      if (assignmentAlreadyLoaded.current === assignmentData.id) return
      assignmentAlreadyLoaded.current = assignmentData.id

      if (AssignmentDTOIsCreativeDTO(assignmentData)) {
        setCreativeInstructionsFromAdmin(assignmentData.creativeInstructionsFromAdmin)
        setCreativeCommentsForAdmin(assignmentData.creativeCommentsForAdmin)
      } else if (AssignmentDTOIsAdministratorDTO(assignmentData)) {
        setCreativeInstructionsFromAdmin(assignmentData.creativeInstructionsFromAdmin)
        setCreativeCommentsForAdmin(assignmentData.creativeCommentsForAdmin)
        setAdminCommentsForEditor(assignmentData.adminCommentsForEditor)
        setAdminInternalComments(assignmentData.adminInternalComments)
      }
    }, [assignmentData, assignment])

    // Update virtual visit URL in admin comments
    useEffect(() => {
      if (virtualVisitURL && isFloorPlan) {
        // Prefill matterport link into comment 
        setAdminCommentsForEditor(previousAdminCommentsForEditor => {
          if (previousAdminCommentsForEditor) return previousAdminCommentsForEditor
          return generateVirtualVisitShareLink(virtualVisitURL, AvailableVirtualVisitLinkTypes.BRANDED)
        })
      }
    }, [AvailableVirtualVisitLinkTypes, generateVirtualVisitShareLink, isFloorPlan, setAdminCommentsForEditor, virtualVisitURL])

    return (
      <GalleryCommentsContext.Provider
        value={{
          adminCommentsForEditor,
          setAdminCommentsForEditor,
          adminInternalComments,
          setAdminInternalComments,
          creativeInstructionsFromAdmin,
          setCreativeInstructionsFromAdmin,
          creativeCommentsForAdmin,
          setCreativeCommentsForAdmin,
          adminCommentsForEditorResponse,
          adminInternalCommentsResponse,
          creativeInstructionsFromAdminResponse,
          editAdminCommentsForEditorMutation,
          editInternalCommentsMutation,
          editCreativeInstructionsFromAdminMutation,
        }}
      >
        {children}
      </GalleryCommentsContext.Provider>
    )
  }
